import { useState, useMemo } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useSettings } from "@/context/SettingsContext";
import { Search, Menu, X, Droplet, LogOut, User, Shield, LayoutGrid, MessageSquare, MessagesSquare, BookOpen, ArrowLeftRight, Trophy, Globe } from "lucide-react";
import { mediaUrl } from "@/lib/api";
import { MessageAdminButton } from "@/components/MessageAdminButton";
import { InstallPrompt } from "@/components/InstallPrompt";
import { RulesGate } from "@/components/RulesGate";

const NAV = [
  { to: "/catalog", label: "Catalog", icon: LayoutGrid },
  { to: "/collection", label: "My Collection", icon: BookOpen, auth: true },
  { to: "/collection/map", label: "My Map", icon: Globe, auth: true },
  { to: "/trades", label: "Trades", icon: ArrowLeftRight, auth: true },
  { to: "/leaderboard", label: "Leaderboard", icon: Trophy },
  { to: "/forum", label: "Forum", icon: MessagesSquare },
  { to: "/messages", label: "Messages", icon: MessageSquare, auth: true },
];

export function Layout({ children }) {
  const { user, logout } = useAuth();
  const { settings } = useSettings();
  const navigate = useNavigate();
  const location = useLocation();
  const [q, setQ] = useState("");
  const [menuOpen, setMenuOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const isAdmin = user && (user.role === "admin" || user.role === "owner");
  const visibleNav = useMemo(() => NAV.filter((n) => !n.auth || user), [user]);

  const submitSearch = (e) => {
    e.preventDefault();
    navigate(`/catalog?search=${encodeURIComponent(q)}`);
    setMenuOpen(false);
  };

  return (
    <div className="min-h-screen flex flex-col">
      <header
        className="sticky top-0 z-50 backdrop-blur-md border-b"
        style={{ background: "var(--header-bg)", color: "var(--header-text)", borderColor: "rgba(180,83,9,0.3)" }}
      >
        <div className="max-w-7xl mx-auto px-4 lg:px-8 py-3 flex items-center gap-2 sm:gap-4">
          <Link to="/" data-testid="brand-logo" className="flex items-center gap-2 shrink-0">
            <Droplet className="w-6 h-6 shrink-0" style={{ color: "var(--honey-500)" }} fill="var(--honey-500)" />
            <span className="font-display text-xl sm:text-2xl font-bold tracking-tight whitespace-nowrap">{settings?.site_name || "Drops of Honey"}</span>
          </Link>

          <form onSubmit={submitSearch} className="hidden md:flex flex-1 max-w-md items-center relative">
            <Search className="absolute left-3 w-4 h-4 opacity-60" />
            <input
              data-testid="nav-search-input"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search CD number, maker, color…"
              className="w-full bg-white/10 border border-white/20 rounded-full pl-9 pr-4 py-2 text-sm placeholder:text-white/50 focus:outline-none focus:bg-white/15"
            />
          </form>

          <nav className="hidden lg:flex items-center gap-1 ml-auto">
            {visibleNav.map((n) => (
              <Link
                key={n.to}
                to={n.to}
                data-testid={`nav-${n.label.toLowerCase().replace(/\s/g, "-")}`}
                className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors hover:bg-white/10 ${location.pathname.startsWith(n.to) ? "bg-white/10" : ""}`}
              >
                {n.label}
              </Link>
            ))}
            {isAdmin && (
              <Link to="/admin" data-testid="nav-admin" className="px-3 py-2 rounded-lg text-sm font-medium transition-colors hover:bg-white/10 flex items-center gap-1">
                <Shield className="w-4 h-4" /> Admin
              </Link>
            )}
          </nav>

          <div className="ml-auto lg:ml-0 flex items-center gap-2">
            {user ? (
              <div className="relative">
                <button data-testid="profile-menu-btn" onClick={() => setProfileOpen((o) => !o)} className="flex items-center gap-2 px-2 py-1.5 rounded-full hover:bg-white/10">
                  {user.avatar_url ? (
                    <img src={mediaUrl(user.avatar_url)} alt="" className="w-7 h-7 rounded-full object-cover" />
                  ) : (
                    <span className="w-7 h-7 rounded-full grid place-items-center text-xs font-bold" style={{ background: "var(--honey-500)", color: "#3a2a10" }}>
                      {(user.username || "?")[0].toUpperCase()}
                    </span>
                  )}
                  <span className="hidden sm:block text-sm font-medium">{user.username}</span>
                </button>
                {profileOpen && (
                  <div className="absolute right-0 mt-2 w-48 surface border rounded-xl shadow-xl overflow-hidden text-[color:var(--page-text)] float-up" onMouseLeave={() => setProfileOpen(false)}>
                    <Link to="/profile" data-testid="menu-profile" className="flex items-center gap-2 px-4 py-2.5 text-sm hover:bg-black/5" onClick={() => setProfileOpen(false)}>
                      <User className="w-4 h-4" /> Profile
                    </Link>
                    <Link to="/collection" className="flex items-center gap-2 px-4 py-2.5 text-sm hover:bg-black/5" onClick={() => setProfileOpen(false)}>
                      <BookOpen className="w-4 h-4" /> My Collection
                    </Link>
                    <button data-testid="logout-btn" onClick={() => { logout(); setProfileOpen(false); navigate("/"); }} className="w-full flex items-center gap-2 px-4 py-2.5 text-sm hover:bg-black/5 text-rose-600">
                      <LogOut className="w-4 h-4" /> Logout
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <>
                <Link to="/login" data-testid="nav-login" className="px-3 py-2 text-sm font-medium hover:bg-white/10 rounded-lg whitespace-nowrap">Sign in</Link>
                <Link to="/register" data-testid="nav-register" className="px-4 py-2 text-sm font-semibold rounded-full whitespace-nowrap" style={{ background: "var(--honey-500)", color: "#3a2a10" }}>Join</Link>
              </>
            )}
            <button className="lg:hidden p-2" data-testid="mobile-menu-btn" onClick={() => setMenuOpen((o) => !o)}>
              {menuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>

        {menuOpen && (
          <div className="lg:hidden border-t border-white/10 px-4 py-3 space-y-1 float-up">
            <form onSubmit={submitSearch} className="flex items-center relative mb-2">
              <Search className="absolute left-3 w-4 h-4 opacity-60" />
              <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search…" className="w-full bg-white/10 border border-white/20 rounded-full pl-9 pr-4 py-2 text-sm placeholder:text-white/50 focus:outline-none" />
            </form>
            {visibleNav.map((n) => (
              <Link key={n.to} to={n.to} className={`block px-3 py-2 rounded-lg transition-colors hover:bg-white/10 ${location.pathname.startsWith(n.to) ? "bg-white/10" : ""}`} onClick={() => setMenuOpen(false)}>{n.label}</Link>
            ))}
            {isAdmin && <Link to="/admin" className={`block px-3 py-2 rounded-lg transition-colors hover:bg-white/10 ${location.pathname.startsWith("/admin") ? "bg-white/10" : ""}`} onClick={() => setMenuOpen(false)}>Admin</Link>}
          </div>
        )}
      </header>

      <main className="flex-1">{children}</main>

      <footer className="border-t mt-16" style={{ borderColor: "var(--page-border)" }}>
        <div className="max-w-7xl mx-auto px-4 lg:px-8 py-10 grid gap-6 md:grid-cols-3 text-sm">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <Droplet className="w-5 h-5 brand-text" fill="currentColor" />
              <span className="font-display text-xl font-bold">{settings?.site_name || "Drops of Honey"}</span>
            </div>
            <p className="text-muted-page">A collector's catalog & community for antique glass and porcelain insulators.</p>
          </div>
          <div>
            <p className="font-tech text-xs uppercase tracking-widest text-muted-page mb-3">Explore</p>
            <div className="space-y-2">
              <Link to="/catalog" className="block honey-underline w-fit">Catalog</Link>
              <Link to="/forum" className="block honey-underline w-fit">Community Forum</Link>
              <Link to="/rules" className="block honey-underline w-fit">Community Rules</Link>
              <Link to="/advertise" className="block honey-underline w-fit">Advertise with us</Link>
            </div>
          </div>
          <div>
            <p className="font-tech text-xs uppercase tracking-widest text-muted-page mb-3">Grading</p>
            <p className="text-muted-page">We grade with the Eagle Cap Collectibles scale (M, A, B, C, D, R, X) plus a 1–10 rarity index.</p>
            <Link to="/terms" className="inline-block mt-3 honey-underline w-fit text-sm">Terms, Copyright & Ownership</Link>
            <Link to="/privacy" className="inline-block mt-1 honey-underline w-fit text-sm">Privacy Policy</Link>
          </div>
        </div>
        <div className="text-center text-xs text-muted-page pb-6 px-4">
          © {new Date().getFullYear()} {settings?.copyright_holder || settings?.site_name || "Drops of Honey"}. All rights reserved. Owned & operated by {settings?.copyright_holder || settings?.site_name || "Drops of Honey"}.
        </div>
      </footer>
      <MessageAdminButton />
      <InstallPrompt />
      <RulesGate />
    </div>
  );
}
